// components/uikit/Typography/Typography.tsx
import clsx from 'clsx'
import React from 'react'

export type TypographyType =
  | '7xl-display'
  | '6xl-display'
  | '5xl-display'
  | '4xl-display'
  | '3xl-heading'
  | '2xl-heading'
  | 'xl-heading'
  | 'l-heading'
  | 's-heading'
  | 'xl-strong'
  | 'l-strong'
  | 'm-strong'
  | 's-strong'
  | '2xs-strong'
  | 'l-midi'
  | 'xs-midi'
  | 'l-body'
  | 's-midi'
  | 'm-body'
  | 's-body'
  | 'xs-body'

interface Props<T extends React.ElementType> {
  tag?: T
  type?: TypographyType | undefined
  className?: string
  color?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

const typeStyles: Record<TypographyType, string> = {
  '7xl-display': 'text-7xl leading-[80px] font-bold',
  '6xl-display': 'text-[56px]',
  '5xl-display': 'text-[48px] leading-[56px] font-bold',
  '4xl-display': 'text-[36px] leading-[48px] font-bold',
  '3xl-heading': 'text-[32px] leading-[44px] font-semibold',
  '2xl-heading': 'text-[24px] leading-[32px] font-semibold',
  'xl-heading': 'text-[20px] leading-[28px] font-bold',
  'l-heading': 'text-[18px] leading-[24px] font-bold',
  's-heading': 'text-[14px] leading-[20px] font-bold',

  'xl-strong': 'text-xl font-semibold',
  'l-strong': 'text-lg font-semibold',
  'm-strong': 'text-base font-semibold',
  's-strong': 'text-sm font-semibold',
  '2xs-strong': 'text-[10px] font-semibold',

  'l-midi': 'text-lg font-medium',
  'xs-midi': 'text-xs font-medium',
  's-midi': 'text-sm font-medium',

  'l-body': 'text-lg font-light',
  'm-body': 'text-base font-light',
  's-body': 'text-sm font-light',
  'xs-body': 'text-xs',
}

export const Typography = <T extends React.ElementType = 'p'>({
  tag,
  type = 'm-body',
  className,
  color,
  style,
  children,
}: Props<T>) => {
  const Component = tag || 'p'

  return (
    <Component className={clsx(typeStyles[type], className)} style={color ? { color, ...style } : style}>
      {children}
    </Component>
  )
}
